import { BOOKING_SETTINGS, EmailIntegrationSection, GeneralInfoView, useToast } from '@elva/ui';
import type { GeneralInfoData } from '@elva/ui';
import { useLocationStore, useWorkspaceLabel } from '@elva/platform';
import { useEffect, useMemo, useRef } from 'react';
import { ElvaSectionState } from '@/components/ElvaSectionState';
import { adaptPracticeGeneralInfo, practiceGeneralInfoToApi } from '../adapters/settingsAdapters';
import { useGeneralInfoEmailIntegration } from '../hooks/useGeneralInfoEmailIntegration';
import { useEmailOauthCallback } from '../hooks/useEmailOauthCallback';
import { useGeneralInfo, useUpdateGeneralInfo } from '../hooks/useGeneralInfo';

const SAVE_DELAY_MS = 600;

/** Practice management › General info (spec 15 §4). Stage's view on the general-info API. */
export function SettingsGeneralInfoPage() {
  const workspaceLabel = useWorkspaceLabel();
  const locationId = useLocationStore((s) => s.selectedCoreLocationId);
  const { data, isLoading, isError } = useGeneralInfo();
  const update = useUpdateGeneralInfo();
  const { toast } = useToast();
  const { isCompletingOauth } = useEmailOauthCallback();
  const { emailIntegration } = useGeneralInfoEmailIntegration();

  const viewData = useMemo(() => (data ? adaptPracticeGeneralInfo(data) : undefined), [data]);

  // The view saves on every change; batch keystrokes into one PUT.
  const pending = useRef<GeneralInfoData | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flush = useRef<() => void>(() => undefined);
  flush.current = () => {
    const next = pending.current;
    pending.current = null;
    if (!next || !data) return;
    update.mutate(practiceGeneralInfoToApi(next, data), {
      onError: () => {
        toast({ title: 'Could not save General info', variant: 'error' });
      },
    });
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      flush.current();
    },
    [],
  );

  return (
    <ElvaSectionState isLoading={isLoading || !viewData} isError={isError}>
      {viewData ? (
        <>
          <GeneralInfoView
            key={locationId ?? 'none'}
            data={viewData}
            location={workspaceLabel}
            bookingLocked={BOOKING_SETTINGS.on}
            onChange={(next) => {
              pending.current = next;
              if (timer.current) clearTimeout(timer.current);
              timer.current = setTimeout(() => {
                flush.current();
              }, SAVE_DELAY_MS);
            }}
          />
          <div className="mt-6">
            <EmailIntegrationSection
              {...emailIntegration}
              connecting={emailIntegration.connecting || isCompletingOauth}
            />
          </div>
        </>
      ) : null}
    </ElvaSectionState>
  );
}
